import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import Button from '../common/Button';

const NavbarContainer = styled.header`
  background-color: ${props => props.isScrolled ? 'rgba(18, 18, 18, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.isScrolled ? 'blur(10px)' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  transition: background-color 0.3s ease, backdrop-filter 0.3s ease;
  box-shadow: ${props => props.isScrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none'};
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  
  img {
    height: 60px;
    width: auto;
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 992px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: #121212;
    padding: 20px 0;
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  }
`;

const StyledNavLink = styled(Link)`
  color: #FFFFFF;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  margin: 0 16px;
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: #2196F3;
  }
  
  @media (max-width: 992px) {
    margin: 12px 0;
  }
`;

// Custom NavLink component that handles both navigation and scrolling
const NavLink = ({ to, children }) => {
  const location = useLocation();
  
  const handleClick = (e) => {
    // Handle home link
    if (to === '/') {
      if (location.pathname === '/') {
        // If already on home page, just scroll to top
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // Otherwise, let the router handle navigation to home
      return;
    }
    
    // Handle section links (features, pricing)
    const sectionId = to.replace('/', ''); // Convert '/features' to 'features'
    const section = document.getElementById(sectionId);
    
    if (section) {
      // If the section exists on the current page, scroll to it
      e.preventDefault();
      section.scrollIntoView({ behavior: 'smooth' });
    }
    // Otherwise, let the router handle navigation
  };
  
  return (
    <StyledNavLink to={to} onClick={handleClick}>
      {children}
    </StyledNavLink>
  );
};

const NavButton = styled.div`
  margin-left: 24px;
  
  @media (max-width: 992px) {
    margin: 16px 0 0;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: #FFFFFF;
  font-size: 24px;
  cursor: pointer;
  
  @media (max-width: 992px) {
    display: block;
  }
`;

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <NavbarContainer isScrolled={isScrolled}>
      <Content>
        <Logo>
          <img src="/logo192.png" alt="CarConsul Logo" />
        </Logo>
        
        <MobileMenuToggle onClick={toggleMenu}>
          {isMenuOpen ? '✕' : '☰'}
        </MobileMenuToggle>
        
        <NavLinks isOpen={isMenuOpen}>
          <NavLink to="/">Home</NavLink>
          <NavLink to="/features">Features</NavLink>
          <NavLink to="/pricing">Pricing</NavLink>
          <NavLink to="/about">About</NavLink>
          <StyledNavLink to="/business">Business</StyledNavLink>
          
          <NavButton>
            <Button 
              variant="primary" 
              size="medium"
              id="nav-cta-button"
              sectionName="navbar"
              onClick={() => {
                window.location.href = 'https://app.carconsul.com';
              }}
            >
              Try
            </Button>
          </NavButton>
        </NavLinks>
      </Content>
    </NavbarContainer>
  );
};

export default Navbar;
